import React, {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import Checkbox from '../checkbox'
import {useForm, useWatch} from 'react-hook-form'
import {Button, Box, Stack, Center, Flex, useDisclosure, Text} from '@chakra-ui/react'
import {EditIcon, GiftIcon} from '../icons'
import Field from '../field'
import useGiftFields from '../../components/forms/useGiftFields'
import ConfirmationModal from '../confirmation-modal'
import GiftMessageView from './gift-message-view'
import useEffectEvent from '../../hooks/use-effect-event'
import {trackGiftMessage} from '../../tracking/analytics'
import useProductItemsDetail from '../../hooks/useProductItemsDetail'
import {useCurrentBasket} from '../../hooks/use-current-basket'

const REMOVE_GIFT_MESSAGE_CONFIRMATION_DIALOG_CONFIG = defineMessages({
    dialogTitle: {
        defaultMessage: 'Do you really want to delete the gift message?',
        id: 'confirmation_modal.gift_msg.title.confirm_remove'
    },
    confirmationMessage: {
        defaultMessage:
            'Deleting the gift message will remove it permanently from your order. This action cannot be undone. Are you sure you want to proceed?',
        id: 'confirmation_modal.gift_msg.message.sure_to_remove'
    },
    primaryActionLabel: {
        defaultMessage: 'Yes, Delete Gift Message',
        id: 'confirmation_modal.gift_msg.action.yes'
    },
    alternateActionLabel: {
        defaultMessage: 'No, Keep Gift Message',
        id: 'confirmation_modal.gift_msg.action.no'
    }
})

const GiftMessage = ({execute, loading, data}) => {
    const [isFormOpen, setFormOpen] = useState(false)

    const form = useForm({
        defaultValues: {
            giftMessage: '',
            giftFrom: '',
            giftRecipientName: '',
            giftRecipientEmail: ''
        }
    })

    const fields = useGiftFields({form})
    const giftFromValue = useWatch({control: form.control, name: 'giftFrom'})
    const {giftMessage: giftMessageErr, giftFrom: giftFromErr} = form.formState.errors
    const {data: basket} = useCurrentBasket()
    const {data: productItemsDetail} = useProductItemsDetail(basket)
    const [messageInputTracked, setMessageInputTracked] = useState(false)
    const intl = useIntl()

    const onFormOpen = useEffectEvent(() => {
        form.setValue('giftMessage', data.giftMessage)
        form.setValue('giftFrom', data.giftFrom)
        form.setValue('giftRecipientName', data.giftRecipientName)
        form.setValue('giftRecipientEmail', data.giftRecipientEmail)
    })

    useEffect(() => {
        if (isFormOpen) {
            onFormOpen()
        }
    }, [isFormOpen, onFormOpen])

    const isSaved = useMemo(() => !isFormOpen && data.giftFrom, [isFormOpen, data.giftFrom])

    const onSubmit = async (data) => {
        await execute({
            c_giftMessageText: data.giftMessage,
            c_giftMessageSender: data.giftFrom,
            c_giftMessage: `${data.giftMessage}\n${data.giftFrom}`,
            c_giftRecipientName: data.giftRecipientName,
            c_giftRecipientEmail: data.giftRecipientEmail
        })
        trackGiftMessage('completed_gift_message_form', basket, productItemsDetail)
        setFormOpen(false)
    }

    const modalProps = useDisclosure()

    const showRemoveConfirmation = modalProps.onOpen

    const handleCheck = (e) => {
        if (e.target.checked) {
            trackGiftMessage('open_gift_message_form', basket, productItemsDetail)
            setFormOpen(true)
        } else {
            showRemoveConfirmation()
        }
    }

    const handleDelete = () => {
        setFormOpen(false)
        execute({
            c_giftMessageText: '',
            c_giftMessageSender: '',
            c_giftMessage: '',
            c_giftRecipientName: '',
            c_giftRecipientEmail: ''
        })
    }

    const handleEdit = () => {
        setFormOpen(true)
    }

    return (
        <Box bg="stokkeBeige.mid">
            <Flex
                w="full"
                justify="space-between"
                align="center"
                px="4"
                py="5"
                data-testid="gift-add-header"
            >
                <Flex>
                    <Flex
                        align="center"
                        justify="center"
                        w="16"
                        h="16"
                        bgColor="stokkeBeige.bright"
                        borderRadius="full"
                    >
                        <GiftIcon w="8" h="8" />
                    </Flex>
                    <Flex direction="column" pl="3" align="start" justify="space-around">
                        <Text
                            as="h2"
                            textStyle="Semi Bold Medium"
                            lineHeight="130%"
                            role={isSaved ? 'status' : undefined}
                        >
                            {isSaved ? (
                                <FormattedMessage
                                    defaultMessage="Gift message saved!"
                                    id="gift_msg.saved"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Make it a gift!"
                                    id="gift_msg.make"
                                />
                            )}
                        </Text>
                        <Box textStyle="Regular Extra Small" lineHeight="130%">
                            <FormattedMessage
                                defaultMessage="Add a complimentary gift message."
                                id="gift_msg.add"
                            />
                        </Box>
                    </Flex>
                </Flex>

                <Checkbox
                    variant="link"
                    size="md"
                    name="addGiftMessage"
                    isChecked={isFormOpen || data.giftFrom || loading}
                    onChange={handleCheck}
                    aria-label={intl.formatMessage({
                        id: 'gift_msg.add',
                        defaultMessage: 'Add a complimentary gift message.'
                    })}
                ></Checkbox>
                <ConfirmationModal
                    {...REMOVE_GIFT_MESSAGE_CONFIRMATION_DIALOG_CONFIG}
                    onPrimaryAction={handleDelete}
                    {...modalProps}
                />
            </Flex>

            {isFormOpen && (
                <Box p="4" pt="2">
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        noValidate
                        data-testid="form-gift-message"
                    >
                        <Stack spacing="4" direction="column">
                            <Field
                                {...fields.giftMessage}
                                inputProps={({onChange}) => ({
                                    autoFocus: true,
                                    onChange(evt) {
                                        if (!messageInputTracked) {
                                            trackGiftMessage(
                                                'started_gift_message_form',
                                                basket,
                                                productItemsDetail
                                            )
                                        }
                                        setMessageInputTracked(true)
                                        return onChange(evt.target.value)
                                    }
                                })}
                            />
                            <Field {...fields.giftFrom} />
                            <Field {...fields.giftRecipientName} />

                            <Field {...fields.giftRecipientEmail} />

                            <Flex textStyle="Regular Extra Small" justify="center">
                                <FormattedMessage
                                    defaultMessage="Your gift message will be sent when the recipients item arrives. No price will be depicted on the invoice the recipient receives."
                                    id="gift_msg.info"
                                />
                            </Flex>
                            <Center w="100%">
                                <Button
                                    w="full"
                                    variant="solid"
                                    size="lg"
                                    type="submit"
                                    disabled={giftMessageErr || giftFromErr || !giftFromValue}
                                    isLoading={loading}
                                    data-testid="gift-save-button"
                                >
                                    <FormattedMessage
                                        defaultMessage="Save"
                                        id="form_action_buttons.button.save"
                                    />
                                </Button>
                            </Center>
                        </Stack>
                    </form>
                </Box>
            )}

            {!isFormOpen && data.giftFrom && (
                <Stack
                    m="4"
                    px="4"
                    py="6"
                    spacing={4}
                    data-testid="gift-summary"
                    bg="stokkeBeige.bright"
                >
                    <GiftMessageView data={data} />
                    <Flex gap="2">
                        <Button
                            leftIcon={<EditIcon boxSize={5} />}
                            variant="link"
                            onClick={handleEdit}
                        >
                            <FormattedMessage defaultMessage="Edit" id="global.edit" />
                        </Button>
                    </Flex>
                </Stack>
            )}
        </Box>
    )
}

export default GiftMessage

GiftMessage.propTypes = {
    data: PropTypes.object,
    execute: PropTypes.func,
    loading: PropTypes.bool
}
